import React, { useEffect, useRef, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

const overlayStack = [];
let overlayCounter = 0;
const overlayListeners = new Set();
let overlaySnapshot = [];

const notifyOverlayListeners = () => {
  overlayListeners.forEach((listener) => listener());
};

const updateOverlaySnapshot = () => {
  overlaySnapshot = overlayStack.map((entry) => ({ ...entry }));
};

const removeOverlay = (id) => {
  const index = overlayStack.findIndex((entry) => entry.id === id);
  if (index !== -1) {
    overlayStack.splice(index, 1);
    updateOverlaySnapshot();
    notifyOverlayListeners();
  }
};

const pushOverlay = (id, name) => {
  removeOverlay(id);
  overlayStack.push({ id, name });
  updateOverlaySnapshot();
  notifyOverlayListeners();
};

const isTopOverlay = (id) => overlayStack[overlayStack.length - 1]?.id === id;
const getOverlaySnapshot = () => overlaySnapshot;
const subscribeToOverlayStack = (listener) => {
  overlayListeners.add(listener);
  return () => overlayListeners.delete(listener);
};

const OverlayWindow = ({
  isOpen,
  onClose,
  children,
  name,
  // Standard scrim: ink at 40% — every overlay in the app uses this tone.
  backdropClassName = 'fixed inset-0 z-50 flex items-center justify-center bg-[var(--ink-900)]/40 p-4',
  panelClassName = 'relative w-full max-h-[90dvh] overflow-y-auto bg-white border-2 border-[var(--ink-900)]',
  // 44px tap target on touch screens, compact 32px from `sm:` up.
  closeButtonClassName = 'absolute top-1.5 right-1.5 sm:top-3 sm:right-3 z-20 text-[var(--ink-500)] hover:text-[var(--ink-900)] hover:bg-[var(--cream)] active:bg-[var(--cream)] w-11 h-11 sm:w-8 sm:h-8 flex items-center justify-center transition-colors',
  closeButtonLabel = 'Schließen',
  closeOnBackdrop = false,
  closeOnEsc = true,
}) => {
  const overlayId = useRef(`overlay-${overlayCounter++}`);
  const overlaySnapshot = useSyncExternalStore(
    subscribeToOverlayStack,
    getOverlaySnapshot,
    getOverlaySnapshot
  );

  useEffect(() => {
    if (!isOpen) {
      removeOverlay(overlayId.current);
      return;
    }
    pushOverlay(overlayId.current, name || 'OverlayWindow');
    return () => {
      removeOverlay(overlayId.current);
    };
  }, [isOpen, name]);

  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (!isTopOverlay(overlayId.current)) return;
        event.preventDefault();
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, closeOnEsc, onClose]);

  const isTop = overlaySnapshot[overlaySnapshot.length - 1]?.id === overlayId.current;

  if (!isOpen) return null;

  const stackIndex = overlaySnapshot.findIndex((entry) => entry.id === overlayId.current);
  const zIndex = stackIndex >= 0 ? 1000 + stackIndex : 1000;

  return createPortal(
    <div
      className={backdropClassName}
      onClick={() => {
        if (closeOnBackdrop && isTopOverlay(overlayId.current)) {
          onClose?.();
        }
      }}
      style={{ zIndex, pointerEvents: isTop ? 'auto' : 'none' }}
      aria-hidden={!isTop}
    >
      <div
        className={panelClassName}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className={closeButtonClassName}
          aria-label={closeButtonLabel}
          title={closeButtonLabel}
        >
          ✕
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default OverlayWindow;
export { getOverlaySnapshot, subscribeToOverlayStack };
